import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { submitLead } from "@/lib/api";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Scale,
  BookOpen,
  Lightbulb,
  GraduationCap,
  BadgeCheck,
  Users,
  Briefcase,
  ArrowRight,
  Mail,
  Phone,
  MapPin,
} from "lucide-react";
import { PROGRAMS } from "@/data/programs";

const LOGO = "/assets/hsnc-logo.webp";
const RECRUITERS_GRID = "/assets/recruiters-grid.png";
const STUDENT_IMG = "/assets/hero-students.png";

const trackLeadFormSubmit = ({ course }) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "lead_form_submit",
    form_name: "hero_lead_form",
    course,
  });
};

const Header = () => (
  <header
    data-testid="site-header"
    className="w-full bg-white border-b border-[#2e326b]/10"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 md:py-6 flex items-center">
      <img
        src={LOGO}
        alt="HSNC University - D.M. Harish School Of Law"
        className="h-24 sm:h-28 md:h-32 lg:h-36 w-auto max-w-[85vw] object-contain"
        data-testid="header-logo"
      />
    </div>
  </header>
);

const HeroForm = () => {
  const [form, setForm] = useState({ name: "", mobile: "", course: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.mobile.trim() || !form.course) {
      toast.error("Please Fill All Fields");
      return;
    }
    const digits = form.mobile.replace(/[^0-9]/g, "");
    if (digits.length < 7) {
      toast.error("Please Enter A Valid Mobile Number");
      return;
    }

    try {
      setLoading(true);
      await submitLead({
        name: form.name.trim(),
        mobile: form.mobile.trim(),
        course: form.course,
      });
      trackLeadFormSubmit({ course: form.course });
      toast.success("Thank You. Our Admissions Team Will Contact You Shortly.");
      setForm({ name: "", mobile: "", course: "" });
    } catch (error) {
      console.error(error);
      toast.error("Something Went Wrong. Please Try Again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      id="lead-form"
      data-testid="hero-lead-form-card"
      className="relative bg-white rounded-2xl shadow-[0_30px_60px_-25px_rgba(46,50,107,0.25)] border border-[#2e326b]/8 p-7 md:p-9 w-full max-w-md ml-auto"
    >
      <div className="absolute -top-3 left-7 bg-[#ffc004] text-[#2e326b] text-[11px] font-bold tracking-wider uppercase px-3 py-1 rounded-full">
        Admissions Open
      </div>
      <h3 className="font-display text-2xl md:text-[28px] font-extrabold text-[#2e326b] leading-tight mb-1">
        Fill In Your Details
      </h3>
      <p className="font-body text-[13px] md:text-sm text-[#4a4e85] leading-relaxed mb-6">
        And Our Admissions Team Will Get In Touch With You.
      </p>
      <form onSubmit={submit} className="space-y-4" data-testid="hero-lead-form">
        <div>
          <label className="block text-[12px] font-semibold text-[#2e326b] mb-1.5 tracking-wide">
            Name
          </label>
          <Input
            data-testid="lead-name-input"
            placeholder="Your Full Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="h-11 bg-white border-[#2e326b]/15 focus-visible:ring-[#ffc004] focus-visible:ring-2 focus-visible:ring-offset-0"
          />
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-[#2e326b] mb-1.5 tracking-wide">
            Mobile Number
          </label>
          <Input
            data-testid="lead-mobile-input"
            placeholder="+91 0000000000"
            inputMode="tel"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            className="h-11 bg-white border-[#2e326b]/15 focus-visible:ring-[#ffc004] focus-visible:ring-2 focus-visible:ring-offset-0"
          />
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-[#2e326b] mb-1.5 tracking-wide">
            Course
          </label>
          <Select
            value={form.course}
            onValueChange={(v) => setForm({ ...form, course: v })}
          >
            <SelectTrigger
              data-testid="lead-course-select"
              className="h-11 bg-white border-[#2e326b]/15 focus:ring-[#ffc004] focus:ring-2 focus:ring-offset-0 text-[#2e326b]"
            >
              <SelectValue placeholder="Select A Course" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="BA LLB" data-testid="course-option-balllb">BA LLB</SelectItem>
              <SelectItem value="LLB" data-testid="course-option-llb">LLB</SelectItem>
              <SelectItem value="LLM" data-testid="course-option-llm">LLM</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="submit"
          disabled={loading}
          data-testid="lead-submit-button"
          className="w-full h-12 bg-[#2e326b] hover:bg-[#1f2256] text-white font-heading font-bold tracking-wide rounded-full transition-all duration-300 shadow-[0_12px_24px_-12px_rgba(46,50,107,0.5)]"
        >
          {loading ? "Submitting..." : "Submit"}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </form>
    </div>
  );
};

const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative overflow-hidden bg-white"
  >
    <div
      className="absolute inset-0 hero-bg-fade pointer-events-none"
      style={{ backgroundImage: `url(${STUDENT_IMG})` }}
    />
    <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#ffc004]/10 blur-3xl pointer-events-none" />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 md:pb-28 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-7 fade-up">
        <div className="inline-flex items-center gap-2 text-[12px] tracking-[0.3em] uppercase font-bold text-[#2e326b]/70 mb-7">
          <span className="h-px w-8 bg-[#ffc004]" />
          D.M. Harish School Of Law
        </div>
        <h1 className="font-display font-extrabold text-[#2e326b] leading-[0.95] tracking-[-0.04em] text-5xl sm:text-6xl lg:text-[88px]">
          BA LLB <span className="text-[#ffc004]">|</span> LLB{" "}
          <span className="text-[#ffc004]">|</span> LLM
        </h1>
        <p className="font-display mt-7 text-2xl md:text-3xl lg:text-[36px] text-[#2e326b] font-extrabold leading-[1.15] tracking-tight max-w-2xl">
          Built On A{" "}
          <span className="sub-heading-highlight">70+ Years Legacy</span> Of
          Excellence
        </p>
        <p className="font-body mt-5 text-base md:text-lg text-[#4a4e85] max-w-xl leading-relaxed">
          Combining Academic Learning With Real-World Exposure
        </p>
        <div className="mt-10 flex items-center gap-6 text-[12px] uppercase tracking-[0.25em] font-semibold text-[#2e326b]/60">
          <span>AICTE Approved</span>
          <span className="h-1 w-1 rounded-full bg-[#ffc004]" />
          <span>Worli, South Mumbai</span>
        </div>
      </div>
      <div className="lg:col-span-5">
        <HeroForm />
      </div>
    </div>
  </section>
);

const ProgramApproach = () => {
  const items = [
    {
      icon: <BookOpen className="w-7 h-7" strokeWidth={1.5} />,
      text: "Practical Learning Integrated With Academic Fundamentals",
    },
    {
      icon: <Lightbulb className="w-7 h-7" strokeWidth={1.5} />,
      text: "Industry-Aligned Curriculum Reflecting Current Practices",
    },
    {
      icon: <Scale className="w-7 h-7" strokeWidth={1.5} />,
      text: "Development Of Analytical Thinking And Decision-Making",
    },
  ];
  return (
    <section
      data-testid="program-approach"
      className="relative bg-white py-24 md:py-36 overflow-hidden"
    >
      <div className="absolute top-1/2 -translate-y-1/2 left-0 w-full h-[55%] bg-[#fffaea] -z-0" />
      <div className="absolute top-1/2 -translate-y-1/2 right-[-12%] w-[420px] h-[420px] rounded-full bg-[#ffc004]/15 blur-3xl -z-0" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end mb-16 md:mb-20">
          <div className="lg:col-span-8">
            <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#2e326b]/60 mb-5">
              <span className="inline-block w-8 h-px bg-[#ffc004] align-middle mr-3" />
              Our Approach
            </p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold text-[#2e326b] leading-[0.98] tracking-[-0.035em]">
              A Premium,
              <br />
              Practice-First Way To{" "}
              <span className="sub-heading-highlight">Study Law</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <div className="hidden lg:block w-24 h-px bg-[#2e326b]/20" />
          </div>
        </div>

        <div className="relative bg-white rounded-[28px] shadow-[0_30px_70px_-40px_rgba(46,50,107,0.25)] border border-[#2e326b]/8 p-8 md:p-14">
          <div className="grid md:grid-cols-3 gap-y-12 md:gap-x-10 lg:gap-x-16">
            {items.map((it, idx) => (
              <div key={idx} className="relative group">
                {idx !== 0 && (
                  <div className="hidden md:block absolute -left-5 lg:-left-8 top-0 bottom-0 w-px bg-[#2e326b]/12" />
                )}
                <div className="flex items-center gap-4 mb-7">
                  <span className="approach-num">0{idx + 1}</span>
                  <span className="h-px flex-1 bg-[#2e326b]/15" />
                </div>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#ffc004]/20 text-[#2e326b] mb-6 group-hover:bg-[#ffc004] transition-colors duration-300">
                  {it.icon}
                </div>
                <p className="font-display text-xl md:text-[22px] text-[#2e326b] font-bold leading-snug max-w-[280px]">
                  {it.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Legacy = () => {
  const items = [
    { icon: <GraduationCap className="w-5 h-5" />, text: "70+ Years Of Academic Legacy" },
    { icon: <BadgeCheck className="w-5 h-5" />, text: "AICTE Approved" },
    { icon: <Users className="w-5 h-5" />, text: "Experienced Faculty" },
    { icon: <Briefcase className="w-5 h-5" />, text: "Dedicated Placement Support" },
  ];
  return (
    <section
      data-testid="legacy-section"
      className="bg-[#f8f9fc] py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-5">
            <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#2e326b]/60 mb-5">
              <span className="inline-block w-8 h-px bg-[#ffc004] align-middle mr-3" />
              Our Foundation
            </p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#2e326b] leading-[0.98] tracking-[-0.035em]">
              A Legacy That{" "}
              <span className="sub-heading-highlight">Shapes Learning</span>
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-x-12 gap-y-10">
            {items.map((it, idx) => (
              <div key={idx} className="flex items-start gap-4">
                <div className="mt-1 flex items-center justify-center w-10 h-10 rounded-full bg-white text-[#2e326b] ring-1 ring-[#2e326b]/10">
                  {it.icon}
                </div>
                <div>
                  <p className="font-display text-xl md:text-2xl text-[#2e326b] font-bold leading-snug tracking-tight">
                    {it.text}
                  </p>
                  <div className="hsnc-divider mt-4 max-w-[100px]" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Excellence = () => {
  const stats = [
    { num: "60+", label: "Internships" },
    { num: "100+", label: "Activities And Events" },
    { num: "50+", label: "Industry Led Expert Sessions" },
    { num: "₹5L+ LPA", label: "Highest Package Offered" },
  ];
  return (
    <section
      data-testid="excellence-section"
      className="bg-[#2e326b] py-24 md:py-32 relative overflow-hidden"
    >
      <div className="absolute -bottom-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#ffc004]/10 blur-3xl" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#ffc004] mb-5">
            <span className="inline-block w-8 h-px bg-[#ffc004] align-middle mr-3" />
            By The Numbers
          </p>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-[0.98] tracking-[-0.035em]">
            Excellence In Numbers
          </h2>
        </div>
        <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
          {stats.map((s, idx) => (
            <div key={idx} className="relative">
              {idx !== 0 && (
                <div className="hidden md:block absolute left-0 top-2 bottom-2 w-px bg-white/15" />
              )}
              <div className="md:pl-6">
                <p
                  className="stat-number text-[#ffc004] text-4xl md:text-5xl lg:text-6xl whitespace-nowrap"
                  data-testid={`stat-num-${idx}`}
                >
                  {s.num}
                </p>
                <p className="mt-3 font-body text-sm md:text-base text-white/85 leading-snug max-w-[180px] font-medium">
                  {s.label}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ProgramContentBlock = ({ program }) => (
  <article
    key={program.key}
    data-testid={`program-content-${program.key.replace(/\s+/g, "-").toLowerCase()}`}
    className="py-12 md:py-16 border-b border-[#2e326b]/10 last:border-b-0"
  >
    <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#ffc004] mb-3">
      {program.label}
    </p>
    <h3 className="font-display text-3xl md:text-4xl lg:text-[44px] font-extrabold text-[#2e326b] leading-[1.05] tracking-[-0.03em]">
      {program.tagline}
    </h3>
    <p className="font-body mt-5 text-[15px] md:text-base text-[#4a4e85] leading-relaxed max-w-2xl">
      {program.overview}
    </p>

    <div className="mt-10 space-y-9">
      <div>
        <p className="sub-heading-highlight font-heading text-base md:text-lg">
          Program Details:
        </p>
        <ul className="mt-4 space-y-2 font-body text-[15px] text-[#4a4e85]">
          {program.details.map((d, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#ffc004] flex-shrink-0" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="sub-heading-highlight font-heading text-base md:text-lg">
          Learning & Program Structure:
        </p>
        <ul className="mt-4 space-y-2 font-body text-[15px] text-[#4a4e85]">
          {program.structure.map((d, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#ffc004] flex-shrink-0" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="sub-heading-highlight font-heading text-base md:text-lg">
          Career Pathways & Exposure:
        </p>
        <ul className="mt-4 space-y-2 font-body text-[15px] text-[#4a4e85]">
          {program.pathways.map((d, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#ffc004] flex-shrink-0" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="sub-heading-highlight font-heading text-base md:text-lg">
          Admissions & Student Support:
        </p>
        <ul className="mt-4 space-y-2 font-body text-[15px] text-[#4a4e85]">
          {program.support.map((d, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#ffc004] flex-shrink-0" />
              <span>{d}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </article>
);

const ProgramsSection = () => {
  const [active, setActive] = useState("BA LLB");
  const refs = {
    "BA LLB": useRef(null),
    LLB: useRef(null),
    LLM: useRef(null),
  };

  useEffect(() => {
    const onScroll = () => {
      const stickyOffset = 160;
      let current = active;
      for (const p of PROGRAMS) {
        const el = refs[p.key].current;
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= stickyOffset && rect.bottom > stickyOffset) {
          current = p.key;
          break;
        }
      }
      if (current !== active) setActive(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const scrollToProgram = (key) => {
    setActive(key);
    const el = refs[key].current;
    if (!el) return;
    const stickyHeight = 110;
    const y = el.getBoundingClientRect().top + window.pageYOffset - stickyHeight;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <section
      data-testid="programs-section"
      className="bg-white relative py-20 md:py-28"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* LEFT — Sticky student image */}
          <aside className="lg:col-span-5 lg:sticky lg:top-24 lg:self-start">
            <div className="program-img-frame aspect-[4/5] md:aspect-[4/5] lg:h-[640px] lg:aspect-auto">
              <img
                src={STUDENT_IMG}
                alt="Indian Law Student In Lawyer Uniform"
                className="w-full h-full object-cover"
                data-testid="program-student-image"
              />
              <div className="absolute bottom-6 left-6 right-6 z-10 text-white">
                <p className="font-heading text-[11px] tracking-[0.35em] uppercase font-bold text-[#ffc004] mb-2">
                  D.M. Harish School Of Law
                </p>
                <p className="font-display text-2xl md:text-3xl font-extrabold leading-tight tracking-tight">
                  Where Future Advocates Are Made
                </p>
              </div>
            </div>
          </aside>

          {/* RIGHT — Heading + Tabs + Content */}
          <div className="lg:col-span-7">
            <div className="pb-6">
              <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#2e326b]/60 mb-5">
                <span className="inline-block w-8 h-px bg-[#ffc004] align-middle mr-3" />
                Programs
              </p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-[#2e326b] leading-[0.98] tracking-[-0.035em]">
                Explore Our{" "}
                <span className="sub-heading-highlight">Law Programs</span>
              </h2>
            </div>

            <div className="sticky-tabs-bar" data-testid="sticky-tabs">
              <div className="flex items-center gap-3 overflow-x-auto px-1">
                {PROGRAMS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => scrollToProgram(p.key)}
                    className={`tab-pill ${active === p.key ? "active" : ""}`}
                    data-testid={`program-tab-${p.key.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              {PROGRAMS.map((p) => (
                <div ref={refs[p.key]} key={p.key} className="scroll-mt-32">
                  <ProgramContentBlock program={p} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Recruiters = () => (
  <section
    data-testid="recruiters-section"
    className="bg-white py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
      <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#2e326b]/60 mb-5">
        Where Our Students Go
      </p>
      <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold text-[#2e326b] leading-[0.98] tracking-[-0.035em]">
        Our <span className="sub-heading-highlight">Recruiters</span>
      </h2>
      <div className="mt-16 max-w-4xl mx-auto">
        <img
          src={RECRUITERS_GRID}
          alt="Recruiter Logos"
          className="w-full h-auto object-contain"
          data-testid="recruiters-image"
        />
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer data-testid="site-footer" className="bg-[#2e326b] text-white">
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid md:grid-cols-3 gap-12 items-start">
      <div>
        <div className="inline-block bg-white rounded-2xl p-5 md:p-6 shadow-[0_20px_40px_-20px_rgba(0,0,0,0.3)]">
          <img
            src={LOGO}
            alt="HSNC University"
            className="h-28 md:h-32 lg:h-36 w-auto object-contain"
            data-testid="footer-logo"
          />
        </div>
      </div>
      <div className="space-y-3 font-body text-sm md:text-[15px] text-white/85 leading-relaxed">
        <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#ffc004] mb-2">
          Address
        </p>
        <div className="flex gap-3">
          <MapPin className="w-4 h-4 mt-1 text-[#ffc004] flex-shrink-0" />
          <p>
            HSNC University, Mumbai D.M. Harish Building, 47, Dr. R. G. Thadani
            Marg, Worli, Mumbai – 400 018.
          </p>
        </div>
      </div>
      <div className="space-y-3 font-body text-sm md:text-[15px] text-white/85">
        <p className="font-heading text-[12px] tracking-[0.35em] uppercase font-bold text-[#ffc004] mb-2">
          Contact
        </p>
        <div className="flex gap-3">
          <Phone className="w-4 h-4 mt-1 text-[#ffc004] flex-shrink-0" />
          <div className="leading-relaxed">
            <p>+91 86559 62501</p>
            <p>+91 86559 62502</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Mail className="w-4 h-4 mt-1 text-[#ffc004] flex-shrink-0" />
          <a
            href="mailto:dmhsl@hsncu.edu.in"
            className="hover:text-[#ffc004] transition-colors"
            data-testid="footer-email"
          >
            dmhsl@hsncu.edu.in
          </a>
        </div>
      </div>
    </div>
    <div className="border-t border-white/10">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 text-[12px] text-white/55 font-body text-center">
        © {new Date().getFullYear()} HSNC University. All Rights Reserved.
      </div>
    </div>
  </footer>
);

const FloatingApply = () => {
  const scrollToForm = () => {
    const el = document.getElementById("lead-form");
    if (el) {
      const y = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };
  return (
    <button
      type="button"
      onClick={scrollToForm}
      className="floating-apply"
      data-testid="floating-apply-now"
      aria-label="Apply Now"
    >
      Apply Now
    </button>
  );
};

function App() {
  return (
    <div className="App font-body">
      <Toaster richColors position="top-center" />
      <Header />
      <main>
        <Hero />
        <ProgramApproach />
        <Legacy />
        <Excellence />
        <ProgramsSection />
        <Recruiters />
      </main>
      <Footer />
      <FloatingApply />
    </div>
  );
}

export default App;
